import {useTranslation} from "react-i18next";
import {Button, Col, Form, Row} from "react-bootstrap";
import React, { useEffect, useMemo, useState } from "react";
import {BoolSelect, FormGroup, Icon, Select, useForm, Yup} from "../../components";
import JsonInput from "../../forms/inputs/JsonInput";
import get from "lodash/get";
import { useGetColumnMappingReferencesQuery } from "../../../api/columnMappingApiSlice";
import { useSelector } from "react-redux";
import BlockTabs from "../../BlockTabs";


const negateFromValue = (value) => {
  if (!value) { return false }
  const { type } = value
  if (['generic_not', 'generic_not_multi'].includes(type.toLowerCase())) {
    return true
  }
  return false
}

const isMultiFromValue = (value) => {
  if (!value) { return false }
  const { type } = value
  if (['generic_multi', 'generic_not_multi'].includes(type.toLowerCase())) {
    return true
  }
  return false
}

const fieldsFromValue = (value) => {
  if (!value) { return [] }
  const { params } = value
  if (params && isMultiFromValue(value)) {
    return get(params, 'properties', [])
  } else if (params && !isMultiFromValue(value)) {
    return [params]
  }
  throw new Error("Invalid params")
}

const yesParams = (property) => ({
  "property": property.property,
  "accepted_values": property.accepted_values,
  "regex": property.regex,
})

const noParams = (property) => {
  let res = {
    "property": property.property,
    "forbidden_values": property.forbidden_values,
    "regex": property.regex,
  }
  if (typeof property.forbid_null !== 'undefined' && property.forbid_null !== null) { res["forbid_null"] = property.forbid_null }
  return res
}

const filterStringify = (negate, properties) => {
  if(!negate && properties.length == 1) {
    const type = 'Generic'
    const params = yesParams(properties[0])
    return [type, params]
  } else if (negate && properties.length == 1) {
    const type = 'Generic_NOT'
    let params = noParams(properties[0])
    return [type, params]
  } else if (!negate && properties.length > 1) {
    const type = 'Generic_Multi'
    const params = {
      "properties": properties.map((p) => (yesParams(p)))
    }
    return [type, params]
  } else if (negate && properties.length > 1) {
    const type = 'Generic_NOT_Multi'
    const params = {
      "properties": properties.map((p) => (noParams(p)))
    }
    return [type, params]
  }
  return ['Generic', {}]
}

const NegatePropertyForm = ({property,  parentFormRef, disabled, onChange, onDelete, propertiesOptions}) => {
  const { t } = useTranslation();
  const {getInputProps, formData} = useForm({
    defaultObject: property,
    initialValues: {
      property: '',
      forbidden_values: [],
      regex: false,
      forbid_null: null
    },
    validationSchema: Yup.object().shape({
      property: Yup.string().required(),
      forbidden_values: Yup.array(Yup.string()).defined(),
      regex: Yup.boolean(),
      forbid_null: Yup.boolean().nullable()
    }),
    disabled: disabled,
    parentFormRef: parentFormRef
  })

  useEffect(() => {
    onChange(formData)
  }, [JSON.stringify(formData)])

  return (
    <Form.Row className="align-items-center">
      <FormGroup as={Col} label={t("stream.field_filter.property")} {...getInputProps("property")}>
        <Select creatable options={propertiesOptions} />
      </FormGroup>
      <FormGroup as={Col} label={t("stream.field_filter.forbidden_values")} {...getInputProps("forbidden_values")}>
        <Select isClearable={false} creatable isMulti />
      </FormGroup>
      <FormGroup as={Col} label={t("stream.field_filter.regex")} {...getInputProps("regex")}>
        <BoolSelect/>
      </FormGroup>
      <FormGroup as={Col} label={t("stream.field_filter.forbid_null")} {...getInputProps("forbid_null")}>
        <BoolSelect isClearable/>
      </FormGroup>
      <Col xs={1}>
        { onDelete && <Button onClick={onDelete}><Icon icon="trash"/></Button> }
      </Col>
    </Form.Row>
  )
}

const AllowPropertyForm = ({property, parentFormRef, disabled, onChange, onDelete, propertiesOptions}) => {
  console.log("onDelete", )
  const { t } = useTranslation();
  const {getInputProps, formData} = useForm({
    defaultObject: property,
    initialValues: {
      property: '',
      accepted_values: [],
      regex: false,
    },
    validationSchema: Yup.object().shape({
      property: Yup.string().required(),
      accepted_values: Yup.array(Yup.string()).min(1).required(),
      regex: Yup.boolean(),
    }),
    disabled: disabled,
    parentFormRef: parentFormRef
  })

  useEffect(() => {
    onChange(formData)
  }, [JSON.stringify(formData)])

  return (
    <Form.Row className="align-items-center">
      <FormGroup as={Col} label={t("stream.field_filter.property")} {...getInputProps("property")}>
        <Select creatable options={propertiesOptions} />
      </FormGroup>
      <FormGroup as={Col} label={t("stream.field_filter.accepted_values")} {...getInputProps("accepted_values")}>
        <Select isClearable={false} creatable isMulti />
      </FormGroup>
      <FormGroup as={Col} label={t("stream.field_filter.regex")} {...getInputProps("regex")}>
        <BoolSelect/>
      </FormGroup>
      <Col xs={1}>
        { onDelete && <Button onClick={onDelete}><Icon icon="trash"/></Button> }
      </Col>
    </Form.Row>
  )
}

const PropetyForm = ({property, negate, parentFormRef, disabled, onChange, onDelete}) => {
  const data_type = useSelector(state => state.dataProduct.data_type)
  const { data, isSuccess, isLoading } = useGetColumnMappingReferencesQuery(data_type, {
    skip: !data_type
  })

  const propertiesOptions = useMemo(() => {
    if (!data || !isSuccess) {
      return []
    }
    const available = Object.keys(get(data, 'available', {}))
    return available.map(property => ({label: property, value: property}))
  }, [data, isSuccess])

  if (negate) {
    return (
      <NegatePropertyForm property={property} parentFormRef={parentFormRef} disabled={disabled} onChange={onChange} onDelete={onDelete} propertiesOptions={propertiesOptions}/>
    )
  } else {
    return (
      <AllowPropertyForm property={property} parentFormRef={parentFormRef} disabled={disabled} onChange={onChange} onDelete={onDelete} propertiesOptions={propertiesOptions}/>
    )
  }
}

export function CustomFilter({disabled, onDelete, value, parentFormRef }) {
  const { t } = useTranslation();
  const [ negate, setNegate ] = useState(negateFromValue(value))
  const [ properties, setProperties] = useState(fieldsFromValue(value))

  useEffect(() => {
    if (properties.length == 0) {
      setProperties([{}])
    }
  }, [])

  const {getInputProps} = useForm({
    defaultObject: value,
    initialValues: {
      name: '',
      type: '',
      params: ''
    },
    validationSchema: Yup.object().shape({
      name: Yup.string().required(),
      type: Yup.string().required(),
      params: Yup.string().json()
    }),
    disabled: disabled,
    parentFormRef: parentFormRef,
    preProcess: (data) => {
      return [{
        ...data,
        params: JSON.parse(data.params)
      }]
    }
  })

  const onPropertyChange = (i) => (e) => {
    setProperties([...properties.slice(0, i), e, ...properties.slice(i + 1)]);
  }

  const onPropertyDelete = (i) => {
    if (i == 0) { return false }
    return () => {
      setProperties([...properties.slice(0, i), ...properties.slice(i + 1)]);
    }
  }
  const {onChange: onParamChange} = getInputProps("params")
  const {onChange: onTypeChange} = getInputProps("type")


  useEffect(()=>{
    const [type, params] = filterStringify(negate, properties)
    onParamChange(params)
    onTypeChange(type)
  }, [JSON.stringify(properties), negate])

  return (
    <>
      <BlockTabs title={t("custom_filter.title")} defaultActiveKey="wyswyg">
        <div key="wyswyg" title={t("stream.field_filter.wysiwyg")}>
          <Form.Row>
            <FormGroup as={Col} md={6} lg={6} label={t('stream.field_filter.matching')} value={negate} onChange={(e) => setNegate(e)}>
              <Select>
                <Option value={false} key="false">{t("stream.field_filter.not_negate")}</Option>
                <Option value={true} key="true">{t("stream.field_filter.negate")}</Option>
              </Select>
            </FormGroup>
            <FormGroup as={Col} md={6} lg={6} style={{ marginTop: '25px' }}>
              <Button onClick={onDelete} disabled={disabled} className="pull-right">{t("delete")}</Button>
            </FormGroup>
          </Form.Row>
          {
            properties.map((property, i) => <PropetyForm key={i} property={property} negate={negate} parentFormRef={parentFormRef} disabled={disabled} onChange={onPropertyChange(i)} onDelete={onPropertyDelete(i)}/>)
          }
          {
            !disabled && <Form.Row className="justify-content-md-center">
              <Button className="pull-right" onClick={() => setProperties([...properties, {}])} disabled={disabled}>{t("add")}</Button>
            </Form.Row>
          }
        </div>
        <div key="raw" title={t("stream.field_filter.raw")}>
          <FormGroup
            input="text"
            md={12}
            label={t("custom_filter.name")}
            {...getInputProps("name")} />

          <FormGroup
            md={12}
            label={t("custom_filter.type")}
            {...getInputProps("type")}>
            <Select isClearable={false} creatable>
              <Option value="Generic">Generic</Option>
              <Option value="Generic_NOT_Multi">Generic_NOT_Multi</Option>
            </Select>
          </FormGroup>
          <JsonInput
            label={t("custom_filter.params")}
            {...getInputProps("params")}/>
          {!disabled && <Button className="pull-right" onClick={onDelete} disabled={disabled}>{t("delete")}</Button>}
        </div>
      </BlockTabs>
    </>
  )
}
